import { UseQueryResult, useQuery } from "@tanstack/react-query";
import { useAuth, authorizedAxios } from "@moonscape/shared";

export interface SyncStatus {
  userid: number;
  exchange: string;
  state: SyncState;
  status: string;
  overall_progress: number | string; // Can be string like "100.00" from API
  current_step: string;
  error_message: string | null;
  created_at: string;
  updated_at: string;
  retry_count?: number;
  stateMachine?: {
    state: string;
    progress: number;
    currentStep: string;
    operations: string[];
  };
  enhanced?: boolean;
  timestamp?: number;
}

export enum SyncState{
    IDLE = 'idle',
    STARTING = 'starting',
    SYNCING = 'syncing',
    IN_PROCESS = 'in_progress',
    PROCESSING = 'processing',
    COMPLETED = 'completed',
    FAILED = 'failed',
}
interface UseSyncStatusProps {
  exchangeId: string;
  enabled?: boolean;
  refetchInterval?: number; // in milliseconds
}

export const useSyncStatusQueryKey = (exchangeId: string) =>
  ["sync-status", exchangeId] as const;

export const useSyncStatus = ({
  exchangeId,
  enabled = true,
  refetchInterval = 3000, // Poll every 3 seconds by default
}: UseSyncStatusProps): {
  isLoading: boolean;
  error: unknown;
  syncStatus?: SyncStatus;
  refetch: () => void;
  isPolling: boolean;
} => {
  const { user, getToken } = useAuth();
  const email = user?.email;

  const getSyncStatus = async (): Promise<SyncStatus> => {
    try {
      const token = await getToken();
      const res = await authorizedAxios(token).get(`/sync/status/${exchangeId}`);

      // API returns { success: true, sync: {...} }
      if (res.data?.success && res.data?.sync) {
        return res.data.sync;
      }

      throw new Error('Invalid sync status response');
    } catch (error) {
      console.error(
        `[useSyncStatus] Failed to fetch sync status for ${exchangeId}:`,
        error
      );
      throw error;
    }
  };

  const {
    isLoading,
    error,
    data,
    refetch,
  }: UseQueryResult<SyncStatus, unknown> = useQuery({
    queryKey: useSyncStatusQueryKey(exchangeId),
    queryFn: getSyncStatus,
    enabled: !!email && !!exchangeId && enabled,
    refetchInterval: (data) => {
      // Only poll if sync is in progress
      const state = data?.state || data?.stateMachine?.state;
      if (state === 'starting' || state === 'in_progress') {
        return refetchInterval;
      }
      // Stop polling if sync is completed, failed, or idle
      return false;
    },
    refetchOnWindowFocus: true,
    staleTime: 0, // Always fetch fresh data
    cacheTime: 1000 * 60, // Keep in cache for 1 minute
  });

  const isPolling =
    (data?.state === 'starting' || data?.state === 'in_progress') ||
    (data?.stateMachine?.state === 'starting' || data?.stateMachine?.state === 'in_progress');

  return {
    isLoading,
    error,
    syncStatus: data,
    refetch,
    isPolling,
  };
};
